import { Platform } from 'react-native';

const DEFAULT_SEPARATOR = '. ';
const DEFAULT_REQUIRED_TEXT = 'required';
const DEFAULT_INVALID_TEXT = 'invalid';
const DEFAULT_DISABLED_TEXT = 'disabled';

/**
 * Options for building an accessibility label with state indicators.
 */
export interface BuildAccessibilityLabelOptions {
    /**
     * The field label text (e.g., "Email", "Password").
     */
    label?: string;

    /**
     * Whether the field is required.
     * When true, `requiredText` is appended to the label.
     */
    required?: boolean;

    /**
     * Pre-translated text for "required" state (e.g., "required", "obligatoire").
     * Defaults to "required" if not provided.
     */
    requiredText?: string;

    /**
     * Pre-translated text for "optional" state (e.g., "optional", "facultatif").
     * Only announced if provided and field is not required.
     * If not provided and field is not required, no optional state is announced.
     */
    optionalText?: string;

    /**
     * Whether the field is in an invalid state.
     * When true, `invalidText` is appended to the label.
     */
    invalid?: boolean;

    /**
     * Pre-translated text for "invalid" state (e.g., "invalid", "invalide").
     * Defaults to "invalid" if not provided.
     */
    invalidText?: string;

    /**
     * Whether the field is disabled.
     * When true, `disabledText` is appended to the label.
     */
    disabled?: boolean;

    /**
     * Pre-translated text for "disabled" state (e.g., "disabled", "desactivado").
     * Defaults to "disabled" if not provided.
     */
    disabledText?: string;

    /**
     * Whether an external label element is used (accessibilityLabelledBy is set).
     *
     * - **Android**: When true, returns undefined so TalkBack uses the external
     *   label (via accessibilityLabelledBy) which must include states.
     * - **iOS**: Ignored. Always uses accessibilityLabel directly since
     *   accessibilityLabelledBy is unreliable on iOS native controls.
     * - **Web**: Handled separately in buildInputAccessibilityProps.
     */
    hasExternalLabel?: boolean;

    /**
     * Whether the input has placeholder text.
     * On Android, TalkBack has a known bug where placeholder text may override
     * accessibilityLabel. When true on Android, returns undefined to rely on
     * the external label element.
     */
    hasPlaceholder?: boolean;

    /**
     * Current value to include in the label (useful for button-style inputs).
     * E.g., for a country picker showing "USA", include it so screen readers announce it.
     */
    value?: string;

    /**
     * Separator between label parts (e.g., between label and "required").
     * Defaults to ". " (period-space) following MADS TextInput pattern.
     * Can be translated for proper word order in different locales.
     */
    labelSeparator?: string;
}

/**
 * Builds an accessibilityLabel with required/optional/invalid states for native mobile platforms.
 *
 * Native iOS/Android don't have aria-invalid/aria-required, so we append state text to the label.
 * This follows the MADS TextInput pattern.
 *
 * ## Platform-specific behavior
 *
 * **iOS**: Always returns a constructed label. iOS's accessibilityLabelledBy is unreliable
 * on native controls, so we always use accessibilityLabel directly.
 *
 * **Android**: Returns undefined when:
 * - `hasExternalLabel` is true (external element conveys state via accessibilityLabelledBy)
 * - `hasPlaceholder` is true (TalkBack bug: placeholder may override accessibilityLabel)
 * In these cases, the external label element must include invalid/required states.
 *
 * **Web**: Always returns undefined. Web uses aria-invalid and aria-describedby instead,
 * which are the idiomatic patterns for web accessibility.
 *
 * ## i18n Note
 *
 * This utility is i18n-agnostic. Consumers should pass pre-translated strings for:
 * - `requiredText` (defaults to "required")
 * - `optionalText` (no default - only announced if provided)
 * - `invalidText` (defaults to "invalid")
 * - `labelSeparator` (defaults to ". ")
 *
 * For proper word order in RTL or other languages, consumers can use their i18n system
 * to format the final label with placeholders.
 *
 * @example
 * // Valid field
 * buildAccessibilityLabel({ label: 'Email' }) // => 'Email'
 *
 * // Required field
 * buildAccessibilityLabel({ label: 'Email', required: true }) // => 'Email. required'
 *
 * // Optional field (when optionalText provided)
 * buildAccessibilityLabel({ label: 'Email', optionalText: 'optional' }) // => 'Email. optional'
 *
 * // Invalid field
 * buildAccessibilityLabel({ label: 'Email', invalid: true }) // => 'Email. invalid'
 *
 * // With value (button-style input)
 * buildAccessibilityLabel({ label: 'Country', value: 'USA' }) // => 'Country. USA'
 *
 * // Android with placeholder - relies on external label
 * buildAccessibilityLabel({ label: 'Email', hasPlaceholder: true }) // => undefined
 */
export function buildAccessibilityLabel({
    label,
    required,
    requiredText,
    optionalText,
    invalid,
    invalidText,
    disabled,
    disabledText,
    hasExternalLabel,
    hasPlaceholder,
    value,
    labelSeparator = DEFAULT_SEPARATOR,
}: BuildAccessibilityLabelOptions): string | undefined {
    // Web uses aria-required/aria-invalid, not accessibilityLabel for states
    if (Platform.OS === 'web') {
        return undefined;
    }

    // Android-specific: when placeholder or external label is present, TalkBack behavior
    // is unreliable with accessibilityLabel. The external label element (pointed to by
    // accessibilityLabelledBy) must include invalid/required states instead.
    if (Platform.OS === 'android' && (hasExternalLabel || hasPlaceholder)) {
        return undefined;
    }

    // iOS: Always construct the label (accessibilityLabelledBy is unreliable on iOS)
    // Android without placeholder/external label: Construct the label normally

    const parts: string[] = [];

    // 1. Base label
    const trimmedLabel = label?.trim();
    if (trimmedLabel) {
        parts.push(trimmedLabel);
    }

    // 2. Required/optional state
    if (required) {
        const text = requiredText?.trim() || DEFAULT_REQUIRED_TEXT;
        parts.push(text);
    } else if (optionalText?.trim()) {
        // Only announce optional if explicitly provided and non-empty
        parts.push(optionalText.trim());
    }

    // 3. Invalid state
    if (invalid) {
        const text = invalidText?.trim() || DEFAULT_INVALID_TEXT;
        parts.push(text);
    }

    // 4. Current value (for button-style inputs)
    const trimmedValue = value?.trim();
    if (trimmedValue) {
        parts.push(trimmedValue);
    }

    // 5. Disabled state
    if (disabled) {
        const text = disabledText?.trim() || DEFAULT_DISABLED_TEXT;
        parts.push(text);
    }

    return parts.length > 0 ? parts.join(labelSeparator) : undefined;
}
